import * as THREE from 'three';

// The two primitives the construction sequence is made of, both animated entirely on the
// GPU from one shared clock (`uTime`, seconds since the hero mounted):
//
//   * BuildLines — line segments that *grow* from their first endpoint to their second,
//     white-hot while they grow and cooling to the ink colour afterwards (a weld bead).
//   * BuildPanels — convex polygons (dome cladding, mirror segments) that fly in from an
//     offset, unfold from their centre and flash once as they lock into place. Glass-like
//     parts (the cladding) are additive and mostly see-through; `solid` parts (the mirror
//     segments) are flat-filled, each a slightly different shade, so the mosaic reads.
//
// Each piece carries its own start time, so a whole structure is one draw call that
// assembles itself in whatever order the builder scheduled.

export type Clock = { value: number };

// Everything above this world height is cut away: the inspector's cut slider lowers it to
// look inside the telescope. Left far above the scene it does nothing.
export const clipY = { value: 1e5 };

export type Segment = { a: THREE.Vector3; b: THREE.Vector3; at: number; dur: number };

const LINE_VERTEX = `
  attribute vec3 aFrom;
  attribute float aAt;
  attribute float aDur;
  uniform float uTime;
  uniform float uClipY;
  varying float vOn;
  varying float vHot;
  varying float vClip;
  void main() {
    float p = clamp((uTime - aAt) / aDur, 0.0, 1.0);
    float e = 1.0 - pow(1.0 - p, 3.0);
    vec3 pos = mix(aFrom, position, e);
    vOn = step(0.0001, p);
    vHot = vOn * (1.0 - smoothstep(0.0, 0.9, uTime - aAt - aDur));
    vClip = uClipY - (modelMatrix * vec4(pos, 1.0)).y;
    vHot = max(vHot, vOn * (1.0 - smoothstep(0.0, 6.0, vClip)));
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }`;

const LINE_FRAGMENT = `
  uniform vec3 uColor;
  uniform vec3 uHotColor;
  uniform float uOpacity;
  varying float vOn;
  varying float vHot;
  varying float vClip;
  void main() {
    if (vOn < 0.5 || vClip < 0.0) discard;
    vec3 c = mix(uColor * uOpacity, uHotColor * 0.8, vHot * 0.6);
    gl_FragColor = vec4(c, 1.0);
  }`;

export type LineStyle = { color: THREE.ColorRepresentation; opacity: number; hot?: THREE.ColorRepresentation };

export function buildLines(segments: Segment[], clock: Clock, style: LineStyle) {
  const positions = new Float32Array(segments.length * 6);
  const from = new Float32Array(segments.length * 6);
  const at = new Float32Array(segments.length * 2);
  const dur = new Float32Array(segments.length * 2);
  segments.forEach((s, i) => {
    positions.set([s.a.x, s.a.y, s.a.z, s.b.x, s.b.y, s.b.z], i * 6);
    // the first vertex stays put; the second slides out of it
    from.set([s.a.x, s.a.y, s.a.z, s.a.x, s.a.y, s.a.z], i * 6);
    at[i * 2] = at[i * 2 + 1] = s.at;
    dur[i * 2] = dur[i * 2 + 1] = Math.max(0.01, s.dur);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aFrom', new THREE.BufferAttribute(from, 3));
  geometry.setAttribute('aAt', new THREE.BufferAttribute(at, 1));
  geometry.setAttribute('aDur', new THREE.BufferAttribute(dur, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: clock,
      uColor: { value: new THREE.Color(style.color) },
      uHotColor: { value: new THREE.Color(style.hot ?? style.color) },
      uOpacity: { value: style.opacity },
      uClipY: clipY,
    },
    vertexShader: LINE_VERTEX,
    fragmentShader: LINE_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const lines = new THREE.LineSegments(geometry, material);
  lines.frustumCulled = false;
  return lines;
}

// Schedules a polyline so it draws continuously from its first point to its last.
export function polyline(
  out: Segment[],
  points: THREE.Vector3[],
  at: number,
  dur: number,
  closed = false,
) {
  const pts = closed ? [...points, points[0]] : points;
  const n = pts.length - 1;
  for (let i = 0; i < n; i++) {
    out.push({ a: pts[i], b: pts[i + 1], at: at + (dur * i) / n, dur: (dur / n) * 1.6 });
  }
}

export function ring(radius: number, y: number, count: number, phase = 0) {
  return Array.from({ length: count }, (_, i) => {
    const a = phase + (i / count) * Math.PI * 2;
    return new THREE.Vector3(Math.sin(a) * radius, y, -Math.cos(a) * radius);
  });
}

export type Panel = {
  vertices: THREE.Vector3[]; // convex, in order
  at: number;
  from: THREE.Vector3; // offset the panel flies in from
};

const PANEL_VERTEX = `
  attribute vec3 aCenter;
  attribute vec3 aFrom;
  attribute float aAt;
  attribute float aShade;
  uniform float uTime;
  uniform float uDur;
  uniform float uClipY;
  varying float vShade;
  varying vec3 vWNormal;
  varying float vClip;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vWorld;
  varying float vOn;
  varying float vHot;
  void main() {
    float p = clamp((uTime - aAt) / uDur, 0.0, 1.0);
    float e = 1.0 - pow(1.0 - p, 4.0);
    vec3 pos = aCenter + (position - aCenter) * mix(0.05, 1.0, e) + aFrom * (1.0 - e) * (1.0 - e);
    vec4 world = modelMatrix * vec4(pos, 1.0);
    vec4 mv = viewMatrix * world;
    vNormal = normalize(normalMatrix * normal);
    vWNormal = normalize(mat3(modelMatrix) * normal);
    vView = normalize(-mv.xyz);
    vWorld = world.xyz;
    vShade = aShade;
    vClip = uClipY - world.y;
    vOn = step(0.0001, p);
    vHot = vOn * (1.0 - smoothstep(0.0, 0.8, uTime - aAt - uDur));
    gl_Position = projectionMatrix * mv;
  }`;

const PANEL_FRAGMENT = `
  uniform vec3 uColor;
  uniform vec3 uRim;
  uniform float uOpacity;
  uniform float uSheen;
  uniform float uTime;
  uniform float uGlow;
  varying vec3 vNormal;
  varying vec3 vWNormal;
  varying vec3 vView;
  varying vec3 vWorld;
  varying float vOn;
  varying float vHot;
  varying float vShade;
  varying float vClip;
  uniform float uMetal;
  uniform vec3 uSlitAcross; // INTERIOR only: world direction across the slit
  uniform vec3 uSlitForward; // INTERIOR only: world direction the slit faces

  // A made-up environment for metal to reflect, in the ink's own tones: a dark ground, a
  // bright band at the horizon, a sky fading up, and a few soft light strips around it.
  vec3 environment(vec3 r) {
    float up = r.y;
    float sky = mix(0.16, 0.42, smoothstep(0.0, 0.8, up));
    float ground = 0.03 + 0.06 * smoothstep(-0.5, 0.0, up);
    float level = up > 0.0 ? sky : ground;
    float horizon = exp(-abs(up + 0.02) * 26.0) * 0.6;
    float az = atan(r.x, r.z);
    float strips = smoothstep(0.6, 0.97, sin(az * 5.0 + 0.6)) * smoothstep(-0.05, 0.3, up) * (1.0 - smoothstep(0.55, 0.9, up)) * 0.4;
  #ifdef INTERIOR
    // what the optics see from inside: the dark dome with its ribs, and the slit — a
    // bright band of sky across it, wherever the dome has turned it
    float across = abs(dot(r, uSlitAcross));
    float behind = dot(r, -uSlitForward);
    float open = (1.0 - smoothstep(0.36, 0.44, across)) * smoothstep(0.05, 0.25, up) * (1.0 - smoothstep(0.2, 0.35, behind));
    float ribs = smoothstep(0.93, 1.0, cos(az * 48.0)) * 0.12 + smoothstep(0.9, 1.0, cos(asin(clamp(up, -1.0, 1.0)) * 24.0)) * 0.08;
    float inside = 0.05 + ribs * smoothstep(-0.2, 0.2, up);
    return uRim * mix(inside, sky * 1.6 + strips, open);
  #else
    return uRim * (level + horizon + strips);
  #endif
  }

  void main() {
    if (vOn < 0.5 || vClip < 0.0) discard;
    float facing = abs(dot(normalize(vNormal), normalize(vView)));
    float rim = pow(1.0 - facing, 2.0);
    // a slow highlight sweeping across reflective parts
    float sweep = pow(max(0.0, sin(vWorld.x * 0.05 - vWorld.z * 0.035 + vWorld.y * 0.02 - uTime * 0.7)), 18.0);
  #ifdef SOLID
    vec3 c = uColor * vShade;
  #ifdef LIT
    // flat-shaded facets: a key light from the upper left, a faint fill from below
    float key = abs(dot(normalize(vWNormal), normalize(vec3(-0.45, 0.8, 0.4))));
    c *= 0.2 + 0.6 * key;
    c += uRim * rim * 0.16;
  #ifdef METAL
    // polished cladding: each flat facet mirrors the environment, so neighbouring panels
    // catch different bands of it, plus a hard highlight from the key light
    vec3 v = normalize(cameraPosition - vWorld);
    vec3 n = normalize(vWNormal);
    if (dot(n, v) < 0.0) n = -n;
    vec3 env = environment(reflect(-v, n));
    float spec = pow(max(dot(n, normalize(normalize(vec3(-0.45, 0.8, 0.4)) + v)), 0.0), 80.0);
    c = mix(c, env * (0.5 + 0.5 * vShade), uMetal) + uRim * spec * 0.55 * uMetal;
  #endif
    float hot = vHot * 0.3;
  #else
    float hot = vHot;
  #endif
    c += uRim * (sweep * uSheen + hot + uGlow + (1.0 - smoothstep(0.0, 5.0, vClip)) * 0.5);
    gl_FragColor = vec4(min(c, vec3(1.0)), uOpacity);
  #else
    vec3 c = uColor * uOpacity * vShade + uRim * rim;
    c += uRim * sweep * uSheen;
    c += uRim * uGlow;
    c += uRim * vHot * 0.6;
    gl_FragColor = vec4(c, 1.0);
  #endif
  }`;

export type PanelStyle = {
  color: THREE.ColorRepresentation;
  rim: THREE.ColorRepresentation;
  opacity: number;
  sheen?: number;
  dur?: number;
  solid?: boolean; // flat-filled and opaque-ish instead of additive glass
  lit?: boolean; // solid only: shade each facet by its normal and add a rim light
  opaque?: boolean; // solid only: write depth, so what is behind the panel is hidden
  shade?: [number, number]; // per-panel brightness range, randomised
  metal?: number; // lit only: 0..1, how much each facet mirrors the environment
  interior?: boolean; // metal only: mirror the inside of the dome and its slit, not the open sky
};

export function buildPanels(panels: Panel[], clock: Clock, style: PanelStyle) {
  let triangles = 0;
  for (const p of panels) triangles += p.vertices.length - 2;
  const positions = new Float32Array(triangles * 9);
  const normals = new Float32Array(triangles * 9);
  const centers = new Float32Array(triangles * 9);
  const froms = new Float32Array(triangles * 9);
  const ats = new Float32Array(triangles * 3);
  const shades = new Float32Array(triangles * 3);
  const [shadeLo, shadeHi] = style.shade ?? [1, 1];
  let seed = panels.length * 7 + 3;
  const nextShade = () => {
    seed = (seed * 16807) % 2147483647;
    return shadeLo + ((seed % 1000) / 1000) * (shadeHi - shadeLo);
  };
  const center = new THREE.Vector3();
  const n = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  let t = 0;
  for (const p of panels) {
    center.set(0, 0, 0);
    for (const v of p.vertices) center.add(v);
    center.divideScalar(p.vertices.length);
    e1.subVectors(p.vertices[1], p.vertices[0]);
    e2.subVectors(p.vertices[2], p.vertices[0]);
    n.crossVectors(e1, e2).normalize();
    const shade = nextShade();
    for (let i = 1; i < p.vertices.length - 1; i++) {
      const tri = [p.vertices[0], p.vertices[i], p.vertices[i + 1]];
      tri.forEach((v, k) => {
        const o = (t * 3 + k) * 3;
        positions.set([v.x, v.y, v.z], o);
        normals.set([n.x, n.y, n.z], o);
        centers.set([center.x, center.y, center.z], o);
        froms.set([p.from.x, p.from.y, p.from.z], o);
        ats[t * 3 + k] = p.at;
        shades[t * 3 + k] = shade;
      });
      t++;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute('aCenter', new THREE.BufferAttribute(centers, 3));
  geometry.setAttribute('aFrom', new THREE.BufferAttribute(froms, 3));
  geometry.setAttribute('aAt', new THREE.BufferAttribute(ats, 1));
  geometry.setAttribute('aShade', new THREE.BufferAttribute(shades, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: clock,
      uDur: { value: style.dur ?? 0.9 },
      uColor: { value: new THREE.Color(style.color) },
      uRim: { value: new THREE.Color(style.rim) },
      uOpacity: { value: style.opacity },
      uSheen: { value: style.sheen ?? 0 },
      uGlow: { value: 0 },
      uMetal: { value: style.metal ?? 0 },
      uSlitAcross: { value: new THREE.Vector3(1, 0, 0) },
      uSlitForward: { value: new THREE.Vector3(0, 0, -1) },
      uClipY: clipY,
    },
    vertexShader: PANEL_VERTEX,
    fragmentShader: PANEL_FRAGMENT,
    defines: style.solid
      ? style.lit
        ? { SOLID: '', LIT: '', ...(style.metal ? { METAL: '' } : {}), ...(style.interior ? { INTERIOR: '' } : {}) }
        : { SOLID: '' }
      : {},
    transparent: !(style.solid && style.opaque),
    depthWrite: !!(style.solid && style.opaque),
    side: THREE.DoubleSide,
    blending: style.solid ? THREE.NormalBlending : THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  return mesh;
}

// Outline of every panel, scheduled to trace itself just before the panel lands.
export function panelOutlines(panels: Panel[], lead = 0.25, dur = 0.5): Segment[] {
  const out: Segment[] = [];
  for (const p of panels) polyline(out, p.vertices, p.at - lead, dur, true);
  return out;
}
